import { LocalNotifications } from '@capacitor/local-notifications'
import { App } from '@capacitor/app'
import type { PrayerTimesDto } from '@/types'
import { api } from '@/services/api'
import {
  loadPrayerPreferences,
  prayerPreferencesFingerprint,
  type LocalPrayerPreferences,
} from '@/services/prayerPreferences'
import { isNativePlatform } from '@/utils/platform'

const SCHEDULE_META_KEY = 'dhikr_prayer_schedule_meta_v1'
const PRAYER_CHANNEL_ID = 'prayer-reminders'
const ID_BASE = 10_000
const SCHEDULE_DAYS = 7
const STALE_HOURS = 24
const LOCATION_MOVE_KM = 20

type PrayerSlot = 'Fajr' | 'Dhuhr' | 'Asr' | 'Maghrib' | 'Isha' | 'Jumuah'

type ScheduleMeta = {
  scheduledUntil: string
  lat: number
  lng: number
  prefsHash: string
  scheduledAt: string
}

const PRAYER_NAMES_AR: Record<PrayerSlot, string> = {
  Fajr: 'الفجر',
  Dhuhr: 'الظهر',
  Asr: 'العصر',
  Maghrib: 'المغرب',
  Isha: 'العشاء',
  Jumuah: 'خطبة الجمعة',
}

const SLOT_INDEX: Record<PrayerSlot, number> = {
  Fajr: 1,
  Dhuhr: 2,
  Asr: 3,
  Maghrib: 4,
  Isha: 5,
  Jumuah: 2,
}

let channelReady = false
let resumeListenerRegistered = false

function readMeta(): ScheduleMeta | null {
  try {
    const raw = localStorage.getItem(SCHEDULE_META_KEY)
    return raw ? JSON.parse(raw) as ScheduleMeta : null
  } catch {
    return null
  }
}

function writeMeta(meta: ScheduleMeta): void {
  localStorage.setItem(SCHEDULE_META_KEY, JSON.stringify(meta))
}

function notificationId(dayIndex: number, slot: PrayerSlot): number {
  return ID_BASE + dayIndex * 10 + SLOT_INDEX[slot]
}

function allPrayerNotificationIds(): number[] {
  const ids: number[] = []
  for (let day = 0; day < 14; day += 1) {
    for (let slot = 1; slot <= 5; slot += 1) {
      ids.push(ID_BASE + day * 10 + slot)
    }
  }
  return ids
}

function distanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => deg * (Math.PI / 180)
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 6371 * 2 * Math.asin(Math.sqrt(a))
}

function parseDay(dateValue: string): Date {
  return new Date(`${dateValue}T12:00:00`)
}

function isFriday(dateValue: string): boolean {
  return parseDay(dateValue).getDay() === 5
}

function buildMessages(
  slot: PrayerSlot,
  minutesBefore: number,
  isArabic: boolean,
): { title: string; body: string } {
  if (slot === 'Jumuah') {
    return isArabic
      ? {
          title: 'تذكير خطبة الجمعة',
          body: `خطبة الجمعة بعد ${minutesBefore} دقيقة. اختر مسجداً قريباً`,
        }
      : {
          title: 'Friday Khutbah Reminder',
          body: `Friday Khutbah in ${minutesBefore} minutes. Find a nearby mosque`,
        }
  }

  if (isArabic) {
    const name = PRAYER_NAMES_AR[slot]
    return {
      title: `حان وقت صلاة ${name}`,
      body: `صلاة ${name} بعد ${minutesBefore} دقيقة. اختر مسجداً قريباً`,
    }
  }

  return {
    title: `${slot} Prayer Time`,
    body: `${slot} prayer in ${minutesBefore} minutes. Find a nearby mosque`,
  }
}

async function ensureChannel(): Promise<void> {
  if (channelReady || !isNativePlatform) return
  await LocalNotifications.createChannel({
    id: PRAYER_CHANNEL_ID,
    name: 'Prayer Reminders',
    description: 'Local prayer time reminders',
    importance: 4,
    sound: 'default',
    vibration: true,
  })
  channelReady = true
}

async function ensurePermission(): Promise<boolean> {
  const status = await LocalNotifications.checkPermissions()
  if (status.display === 'granted') return true
  const requested = await LocalNotifications.requestPermissions()
  return requested.display === 'granted'
}

function shouldReschedule(meta: ScheduleMeta | null, lat: number, lng: number, prefs: LocalPrayerPreferences): boolean {
  if (!meta) return true
  if (meta.prefsHash !== prayerPreferencesFingerprint(prefs)) return true
  if (distanceKm(meta.lat, meta.lng, lat, lng) > LOCATION_MOVE_KM) return true

  const scheduledUntil = new Date(meta.scheduledUntil).getTime()
  const staleAt = Date.now() + STALE_HOURS * 60 * 60 * 1000
  return scheduledUntil < staleAt
}

function prayerEntriesForDay(
  day: PrayerTimesDto,
  prefs: LocalPrayerPreferences,
  jumuahEnabled: boolean,
  khutbahLeadMinutes: number,
): Array<{ slot: PrayerSlot; at: Date }> {
  const entries: Array<{ slot: PrayerSlot; at: Date }> = []
  const add = (slot: PrayerSlot, iso: string) => entries.push({ slot, at: new Date(iso) })

  if (prefs.fajrEnabled) add('Fajr', day.fajr)

  const fridayJumuah = isFriday(day.date) && jumuahEnabled && prefs.dhuhrEnabled
  if (fridayJumuah) {
    const khutbahStart = new Date(new Date(day.dhuhr).getTime() - khutbahLeadMinutes * 60_000)
    add('Jumuah', khutbahStart.toISOString())
  } else if (prefs.dhuhrEnabled) {
    add('Dhuhr', day.dhuhr)
  }

  if (prefs.asrEnabled) add('Asr', day.asr)
  if (prefs.maghribEnabled) add('Maghrib', day.maghrib)
  if (prefs.ishaEnabled) add('Isha', day.isha)

  return entries
}

export async function schedulePrayerNotifications(
  lat: number,
  lng: number,
  isAuthenticated: boolean,
  locale: string,
  force = false,
): Promise<boolean> {
  if (!isNativePlatform) return false

  const prefs = await loadPrayerPreferences(isAuthenticated)
  const meta = readMeta()
  if (!force && !shouldReschedule(meta, lat, lng, prefs)) {
    return true
  }

  if (!prefs.prayerNotificationsEnabled) {
    await cancelPrayerNotifications()
    return true
  }

  const permitted = await ensurePermission()
  if (!permitted) return false

  await ensureChannel()

  const { data } = await api.getPrayerTimesRange(lat, lng, SCHEDULE_DAYS, undefined, prefs.calculationMethod)
  if (!data.success || !data.data?.days?.length) {
    return false
  }

  const now = Date.now()
  const isArabic = locale.startsWith('ar')
  const notifications = data.data.days.flatMap((day, dayIndex) =>
    prayerEntriesForDay(day, prefs, data.data!.jumuahEnabled, data.data!.jumuahKhutbahLeadMinutes)
      .map(({ slot, at }) => {
        const notifyAt = new Date(at.getTime() - prefs.minutesBefore * 60_000)
        if (notifyAt.getTime() <= now) return null
        const { title, body } = buildMessages(slot, prefs.minutesBefore, isArabic)
        const reminderType = slot === 'Jumuah' ? 'jumuah_reminder' : 'prayer_reminder'
        return {
          id: notificationId(dayIndex, slot),
          title,
          body,
          schedule: { at: notifyAt },
          channelId: PRAYER_CHANNEL_ID,
          sound: 'default',
          extra: {
            type: reminderType,
            prayerName: slot,
          },
        }
      })
      .filter((item): item is NonNullable<typeof item> => item != null),
  )

  await LocalNotifications.cancel({ notifications: allPrayerNotificationIds().map((id) => ({ id })) })

  if (notifications.length > 0) {
    await LocalNotifications.schedule({ notifications })
  }

  const lastDay = data.data.days[data.data.days.length - 1]!
  writeMeta({
    scheduledUntil: lastDay.isha,
    lat,
    lng,
    prefsHash: prayerPreferencesFingerprint(prefs),
    scheduledAt: new Date().toISOString(),
  })

  return true
}

export async function cancelPrayerNotifications(): Promise<void> {
  if (!isNativePlatform) return
  await LocalNotifications.cancel({ notifications: allPrayerNotificationIds().map((id) => ({ id })) })
  localStorage.removeItem(SCHEDULE_META_KEY)
}

export function registerPrayerScheduleLifecycle(
  getCoords: () => Promise<{ lat: number; lng: number }>,
  isAuthenticated: () => boolean,
  getLocale: () => string,
): void {
  if (!isNativePlatform || resumeListenerRegistered) return
  resumeListenerRegistered = true

  App.addListener('resume', async () => {
    try {
      const { lat, lng } = await getCoords()
      await schedulePrayerNotifications(lat, lng, isAuthenticated(), getLocale())
    } catch (error) {
      console.warn('Prayer schedule refresh on resume failed:', error)
    }
  })
}

export async function bootstrapPrayerNotifications(
  getCoords: () => Promise<{ lat: number; lng: number }>,
  isAuthenticated: boolean,
  locale: string,
): Promise<void> {
  if (!isNativePlatform) return
  try {
    const { lat, lng } = await getCoords()
    await schedulePrayerNotifications(lat, lng, isAuthenticated, locale)
  } catch (error) {
    console.warn('Initial prayer schedule failed:', error)
  }
}
